import React, { useState, useEffect } from 'react';
import { calendarAPI, casesAPI } from '../../lib/api';
import { cn, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Calendar,
  FileText as FileTemplate,
  Play,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Briefcase,
  Clock,
  AlertTriangle,
  Gavel,
  Users,
  Scale,
  Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

const TEMPLATE_ICONS = {
  civil_litigation: Scale,
  family_law: Users,
  corporate_transaction: Building2,
  personal_injury: AlertTriangle,
  criminal_defence: Gavel,
};

const EVENT_TYPE_COLORS = {
  court_appearance: 'bg-purple-100 text-purple-700',
  filing_deadline: 'bg-red-100 text-red-700',
  limitation_deadline: 'bg-red-200 text-red-800',
  hearing: 'bg-violet-100 text-violet-700',
  client_meeting: 'bg-blue-100 text-blue-700',
  internal_task: 'bg-slate-100 text-slate-700',
  deadline: 'bg-orange-100 text-orange-700',
};

export const CaseCalendarTemplates = ({ caseId, caseName, onGenerated }) => {
  const [templates, setTemplates] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCase, setSelectedCase] = useState(caseId || '');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [jurisdiction, setJurisdiction] = useState('');
  const [jurisdictions, setJurisdictions] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [templatesRes, casesRes, jurisdictionsRes] = await Promise.all([
        calendarAPI.getCalendarTemplates(),
        casesAPI.getAll(),
        calendarAPI.getJurisdictions(),
      ]);
      setTemplates(templatesRes.data.templates || []);
      setCases(casesRes.data || []);
      setJurisdictions(jurisdictionsRes.data.jurisdictions || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setShowConfirmDialog(true);
  };

  const handleGenerateCalendar = async () => {
    if (!selectedCase) {
      toast.error('Please select a case');
      return;
    }
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    setGenerating(true);
    try {
      const response = await calendarAPI.generateCaseCalendar(
        selectedCase,
        selectedTemplate.id,
        new Date(startDate).toISOString(),
        jurisdiction || null
      );
      setGenerationResult(response.data);
      toast.success(`Created ${response.data.events_created.length} calendar events`);
      onGenerated?.(response.data);
    } catch (err) {
      console.error('Failed to generate calendar:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate calendar');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
          <FileTemplate className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900">Auto-Generate Case Calendar</h3>
          <p className="text-sm text-slate-500">Create standard milestones based on case type</p>
        </div>
      </div>

      {/* Case Selection (if not pre-selected) */}
      {!caseId && (
        <div className="space-y-2">
          <Label>Select Case</Label>
          <Select value={selectedCase} onValueChange={setSelectedCase}>
            <SelectTrigger data-testid="case-select">
              <Briefcase className="h-4 w-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Choose a case" />
            </SelectTrigger>
            <SelectContent>
              {cases.map((c) => (
                <SelectItem key={c.case_id} value={c.case_id}>
                  {c.title || c.case_number}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => {
          const IconComponent = TEMPLATE_ICONS[template.id] || Calendar;
          const isSelected = selectedTemplate?.id === template.id;
          
          return (
            <Card 
              key={template.id}
              className={cn(
                "cursor-pointer transition-all hover:shadow-md",
                isSelected && "ring-2 ring-blue-500 bg-blue-50"
              )}
              onClick={() => handleSelectTemplate(template)}
              data-testid={`template-${template.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "h-10 w-10 rounded-lg flex items-center justify-center",
                    isSelected ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-600"
                  )}>
                    <IconComponent className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-900">{template.name}</h4>
                    <p className="text-sm text-slate-500 mt-0.5">
                      {template.milestone_count} milestones
                    </p>
                  </div>
                  {isSelected && (
                    <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0" />
                  )}
                </div>

                {/* Milestone Preview */}
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-slate-500 mb-2">Key milestones:</p>
                  <div className="flex flex-wrap gap-1">
                    {template.milestones.slice(0, 3).map((milestone, idx) => (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className={cn("text-xs", EVENT_TYPE_COLORS[milestone.event_type])}
                      >
                        {milestone.title.length > 20 ? milestone.title.substring(0, 20) + '...' : milestone.title}
                      </Badge>
                    ))}
                    {template.milestones.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{template.milestones.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileTemplate className="h-5 w-5" />
              Generate Calendar: {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Start Date */}
            <div className="space-y-2">
              <Label>Start Date (Case Open Date)</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="calendar-start-date"
              />
              <p className="text-xs text-slate-500">
                All milestone dates will be calculated from this date
              </p>
            </div>

            {/* Jurisdiction (Optional) */}
            <div className="space-y-2">
              <Label>Jurisdiction (Optional)</Label>
              <Select value={jurisdiction} onValueChange={setJurisdiction}>
                <SelectTrigger>
                  <SelectValue placeholder="Skip weekends/holidays for..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {jurisdictions.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.name} ({j.country})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Selecting a jurisdiction will skip court holidays when calculating dates
              </p>
            </div>

            {/* Milestones Preview */}
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="milestones" className="border rounded-lg">
                <AccordionTrigger className="px-4 py-2 text-sm">
                  View {selectedTemplate?.milestone_count} milestones to be created
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {selectedTemplate?.milestones.map((milestone, idx) => (
                        <div 
                          key={idx}
                          className={cn(
                            "p-2 rounded border text-sm",
                            EVENT_TYPE_COLORS[milestone.event_type] || 'bg-slate-50'
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{milestone.title}</span>
                            <Badge variant="outline" className="text-xs">
                              Day +{milestone.offset_days}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs opacity-80">
                            <span className="capitalize">{milestone.event_type.replace(/_/g, ' ')}</span>
                            <span>•</span>
                            <span className="capitalize">{milestone.priority} priority</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateCalendar} 
              disabled={generating || !selectedCase}
              data-testid="generate-calendar-btn"
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Generate Calendar
            </Button>
          </DialogFooter>

          {/* Generation Result */}
          {generationResult && (
            <Card className="bg-green-50 border-green-200 mt-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">
                    Created {generationResult.events_created.length} events!
                  </span>
                </div>
                <ScrollArea className="h-[150px]">
                  <div className="space-y-1">
                    {generationResult.events_created.map((event, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-green-100">
                        <span className="text-green-800">{event.title}</span>
                        <span className="text-green-600 text-xs">
                          {new Date(event.date).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <Button 
                  className="w-full mt-3" 
                  variant="outline"
                  onClick={() => {
                    setShowConfirmDialog(false);
                    setGenerationResult(null);
                  }}
                >
                  Done
                </Button>
              </CardContent>
            </Card>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CaseCalendarTemplates;
